/**
 * ComparisonAnalyticsView (Iter-127) — full-page comparison dashboard.
 *
 * Design goals (per owner feedback):
 *  - LESS clutter: only 4 headline tiles (New Quotes, Closed Value,
 *    Lost Value, Conversion Rate) not 8. Secondary metrics fold into
 *    tile subtitles.
 *  - Category share = animated PIE with slice pop-out + % (owner
 *    explicitly asked for pie, not bars).
 *  - Cleaner status donuts.
 *  - Salesperson head-to-head bar chart PLUS an individual "Present to
 *    this salesperson" mode — dropdown → full-screen personal review with
 *    good/growth takeaways generated from their period-over-period stats.
 *    Perfect for 1-on-1 meetings.
 *  - Pipeline Health card sits at the top of this page (it was mistakenly
 *    dropped on the classic Analytics before — belongs with the deep dive).
 */
import React, { useEffect, useMemo, useState, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Skeleton } from '../ui/skeleton';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { toast } from 'sonner';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../ui/select';
import {
  ResponsiveContainer,
  BarChart, Bar,
  LineChart, Line,
  PieChart, Pie, Cell,
  XAxis, YAxis, CartesianGrid, Tooltip, Legend,
} from 'recharts';
import {
  GitCompareArrows, TrendingUp, TrendingDown, Minus,
  Sparkles, AlertTriangle, CheckCircle2, ArrowLeft,
  Crown, Boxes, ArrowRight, User, ThumbsUp, Target, Presentation, FileDown, MessageCircle,
} from 'lucide-react';
import { api, formatINR } from '../../lib/store';
import { logger } from '../../lib/logger';
import AnimatedCategoryPie from './AnimatedCategoryPie';
import PipelineHealthCard from './PipelineHealthCard';

// ------------------ helpers ------------------
const short = (n) => {
  if (n === null || n === undefined || isNaN(n)) return '₹0';
  const a = Math.abs(n);
  if (a >= 10000000) return `₹${(n / 10000000).toFixed(2)}Cr`;
  if (a >= 100000) return `₹${(n / 100000).toFixed(2)}L`;
  if (a >= 1000) return `₹${(n / 1000).toFixed(0)}K`;
  return `₹${Math.round(n)}`;
};
const isoToday = () => {
  const d = new Date();
  const off = d.getTimezoneOffset();
  const local = new Date(d.getTime() - off * 60000);
  return local.toISOString().slice(0, 10);
};
const addDays = (yyyymmdd, delta) => {
  const [y, m, d] = yyyymmdd.split('-').map(Number);
  const dt = new Date(Date.UTC(y, m - 1, d));
  dt.setUTCDate(dt.getUTCDate() + delta);
  return dt.toISOString().slice(0, 10);
};
const lastDayOfMonth = (y, m) => new Date(Date.UTC(y, m, 0)).getUTCDate();
const buildPresets = (todayStr) => {
  const [ty, tm, td] = todayStr.split('-').map(Number);
  const aStart = `${ty}-${String(tm).padStart(2, '0')}-01`;
  const prevMonth = tm === 1 ? 12 : tm - 1;
  const prevYear = tm === 1 ? ty - 1 : ty;
  const bClamp = Math.min(td, lastDayOfMonth(prevYear, prevMonth));
  const bStart = `${prevYear}-${String(prevMonth).padStart(2, '0')}-01`;
  const bEnd = `${prevYear}-${String(prevMonth).padStart(2, '0')}-${String(bClamp).padStart(2, '0')}`;
  const now = new Date(Date.UTC(ty, tm - 1, td));
  const weekday = now.getUTCDay() || 7;
  const thisWeekStart = new Date(now); thisWeekStart.setUTCDate(now.getUTCDate() - (weekday - 1));
  const lastWeekStart = new Date(thisWeekStart); lastWeekStart.setUTCDate(thisWeekStart.getUTCDate() - 7);
  const lastWeekEnd = new Date(thisWeekStart); lastWeekEnd.setUTCDate(thisWeekStart.getUTCDate() - 1);
  const last6MoStart = addDays(todayStr, -179);
  const prev6MoEnd = addDays(last6MoStart, -1);
  const prev6MoStart = addDays(prev6MoEnd, -179);
  return {
    mtd:      { label: 'This month vs last month',    a_start: aStart, a_end: todayStr, b_start: bStart, b_end: bEnd },
    week:     { label: 'This week vs last week',      a_start: thisWeekStart.toISOString().slice(0,10), a_end: todayStr, b_start: lastWeekStart.toISOString().slice(0,10), b_end: lastWeekEnd.toISOString().slice(0,10) },
    last7:    { label: 'Last 7d vs prev 7d',           a_start: addDays(todayStr, -6), a_end: todayStr, b_start: addDays(todayStr, -13), b_end: addDays(todayStr, -7) },
    last30:   { label: 'Last 30d vs prev 30d',         a_start: addDays(todayStr, -29), a_end: todayStr, b_start: addDays(todayStr, -59), b_end: addDays(todayStr, -30) },
    last6mo:  { label: 'Last 6 months vs prev 6 months', a_start: last6MoStart, a_end: todayStr, b_start: prev6MoStart, b_end: prev6MoEnd },
  };
};
const monthLabelShort = (yyyymmdd) => {
  if (!yyyymmdd) return '';
  const [, m, d] = yyyymmdd.split('-');
  const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  return `${parseInt(d, 10)} ${months[parseInt(m, 10) - 1]}`;
};
const rangeLabel = (start, end) => start === end ? monthLabelShort(start) : `${monthLabelShort(start)} – ${monthLabelShort(end)}`;

const A_COLOR = '#556B2F';
const B_COLOR = '#DAA520';

// ------------------ delta chip ------------------
function DeltaChip({ pct, size = 'md' }) {
  const style = size === 'sm' ? 'text-[10px] px-1.5 py-0.5' : 'text-xs px-2 py-0.5';
  if (pct === null || pct === undefined) {
    return <span className={`inline-flex items-center gap-0.5 rounded-full bg-[#E2E6D9] text-[#5C6150] font-semibold ${style}`}>—</span>;
  }
  if (pct === 0) {
    return <span className={`inline-flex items-center gap-0.5 rounded-full bg-[#F3F4EE] text-[#5C6150] font-semibold ${style}`}><Minus className="w-3 h-3" /> 0%</span>;
  }
  const up = pct > 0;
  return (
    <motion.span
      initial={{ opacity: 0, y: -4 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.4 }}
      className={`inline-flex items-center gap-0.5 rounded-full font-semibold ${style} ${up ? 'bg-[#2E7D32]/10 text-[#2E7D32]' : 'bg-[#C62828]/10 text-[#C62828]'}`}
    >
      {up ? <TrendingUp className="w-3 h-3" /> : <TrendingDown className="w-3 h-3" />}
      {up ? '+' : ''}{pct}%
    </motion.span>
  );
}

function CountUp({ value, format = (n) => n.toLocaleString('en-IN'), duration = 0.8 }) {
  const [display, setDisplay] = useState(0);
  useEffect(() => {
    let raf;
    const start = performance.now();
    const to = Number(value) || 0;
    const step = (now) => {
      const t = Math.min(1, (now - start) / (duration * 1000));
      setDisplay(to * (1 - Math.pow(1 - t, 3)));
      if (t < 1) raf = requestAnimationFrame(step);
    };
    raf = requestAnimationFrame(step);
    return () => cancelAnimationFrame(raf);
  }, [value, duration]);
  return <span>{format(display)}</span>;
}

// ------------------ headline tile (4 only) ------------------
function BigTile({ label, valueA, valueB, delta, format = (n) => Math.round(n).toLocaleString('en-IN'), tone, sub, testId }) {
  const tones = {
    olive: { grad: 'from-[#556B2F] to-[#8FA057]', text: 'text-[#556B2F]' },
    green: { grad: 'from-[#2E7D32] to-[#66BB6A]', text: 'text-[#2E7D32]' },
    red:   { grad: 'from-[#C62828] to-[#EF5350]', text: 'text-[#C62828]' },
    blue:  { grad: 'from-[#1F4E79] to-[#5F8DBB]', text: 'text-[#1F4E79]' },
  }[tone];
  return (
    <motion.div
      initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}
      className="rounded-2xl border border-[#E2E6D9] bg-white overflow-hidden shadow-sm hover:shadow-lg transition-shadow"
      data-testid={testId}
    >
      <div className={`h-1.5 bg-gradient-to-r ${tones.grad}`} />
      <div className="p-4">
        <div className="flex items-start justify-between gap-2 mb-2">
          <p className={`text-[11px] uppercase tracking-widest font-bold ${tones.text}`}>{label}</p>
          <DeltaChip pct={delta} />
        </div>
        <p className="text-3xl sm:text-4xl font-bold text-[#1A1C16] font-mono-data leading-none">
          <CountUp value={valueA} format={format} />
        </p>
        <p className="text-[11px] text-[#8F9485] mt-2">
          <span className="text-[#5C6150] font-semibold">Prev:</span> {format(valueB)}
          {sub && <span className="ml-2 text-[#8F9485]">· {sub}</span>}
        </p>
      </div>
    </motion.div>
  );
}

// ------------------ period picker ------------------
function PeriodPicker({ preset, dates, onPreset, onDatesChange }) {
  const chips = [
    { key: 'mtd', label: 'MTD vs Last Month' },
    { key: 'week', label: 'This Wk vs Last Wk' },
    { key: 'last7', label: '7d vs 7d' },
    { key: 'last30', label: '30d vs 30d' },
    { key: 'last6mo', label: '6mo vs 6mo' },
    { key: 'custom', label: 'Custom' },
  ];
  return (
    <Card className="border-[#E2E6D9] bg-gradient-to-br from-white to-[#F9FAF7]" data-testid="compare-period-picker">
      <CardContent className="pt-4 pb-4">
        <div className="flex items-center gap-2 mb-3">
          <div className="p-1.5 rounded-lg bg-[#DAA520]/10">
            <GitCompareArrows className="w-4 h-4 text-[#DAA520]" />
          </div>
          <div>
            <p className="text-[11px] uppercase tracking-wider text-[#8F9485] font-semibold">Comparing</p>
            <p className="text-sm font-bold text-[#1A1C16]">
              <span className="text-[#556B2F]">{rangeLabel(dates.a_start, dates.a_end)}</span>
              <ArrowRight className="w-3 h-3 inline mx-2 text-[#8F9485]" />
              <span className="text-[#8B6914]">vs {rangeLabel(dates.b_start, dates.b_end)}</span>
            </p>
          </div>
        </div>
        <div className="flex flex-wrap gap-1.5">
          {chips.map(c => (
            <button
              key={c.key}
              onClick={() => onPreset(c.key)}
              data-testid={`compare-preset-${c.key}`}
              className={`text-xs px-3 py-1.5 rounded-full transition-all ${preset === c.key ? 'bg-[#556B2F] text-white shadow-sm' : 'bg-white border border-[#E2E6D9] text-[#5C6150] hover:border-[#556B2F]'}`}
            >
              {c.label}
            </button>
          ))}
        </div>
        <AnimatePresence>
          {preset === 'custom' && (
            <motion.div initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: 'auto' }} exit={{ opacity: 0, height: 0 }} className="mt-3 pt-3 border-t border-[#E2E6D9] overflow-hidden">
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                {['a_start', 'a_end', 'b_start', 'b_end'].map(k => (
                  <div key={k}>
                    <Label className="text-[10px] uppercase tracking-wider text-[#8F9485]">
                      {k === 'a_start' ? 'A Start' : k === 'a_end' ? 'A End' : k === 'b_start' ? 'B Start' : 'B End'}
                    </Label>
                    <Input type="date" value={dates[k]} onChange={(e) => onDatesChange({ ...dates, [k]: e.target.value })} className="text-xs h-8" data-testid={`compare-date-${k}`} />
                  </div>
                ))}
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </CardContent>
    </Card>
  );
}

// ------------------ status donut side-by-side ------------------
function _StatusDonutOne({ p, sub }) {
  const buildData = (pp) => {
    const c = pp.summary.status_counts;
    return [
      { name: 'Closed', value: c.closed, color: '#2E7D32' },
      { name: 'Pending', value: c.pending, color: '#F9A825' },
      { name: 'Lost', value: c.lost, color: '#C62828' },
    ];
  };
  const total = Math.max(1, p.summary.total_quotations);
  const closedPct = ((p.summary.status_counts.closed / total) * 100).toFixed(0);
  return (
    <div className="text-center">
      <p className="text-xs font-semibold text-[#5C6150] mb-1">{sub}</p>
      <div className="h-44 relative">
        <ResponsiveContainer width="100%" height="100%">
          <PieChart>
            <Pie data={buildData(p)} dataKey="value" nameKey="name" innerRadius={42} outerRadius={70} paddingAngle={3} isAnimationActive animationDuration={800}>
              {buildData(p).map(d => <Cell key={d.name} fill={d.color} />)}
            </Pie>
            <Tooltip formatter={(v, n) => [`${v}`, n]} contentStyle={{ fontSize: '11px', borderRadius: 6, borderColor: '#E2E6D9' }} />
          </PieChart>
        </ResponsiveContainer>
        <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
          <div>
            <p className="text-3xl font-bold text-[#1A1C16] leading-none"><CountUp value={closedPct} format={(n) => Math.round(n)} />%</p>
            <p className="text-[9px] uppercase tracking-wider text-[#8F9485]">closed</p>
          </div>
        </div>
      </div>
      <div className="flex justify-center gap-2 text-[10px] mt-1">
        <span className="text-[#2E7D32]">● {p.summary.status_counts.closed}</span>
        <span className="text-[#F9A825]">● {p.summary.status_counts.pending}</span>
        <span className="text-[#C62828]">● {p.summary.status_counts.lost}</span>
      </div>
    </div>
  );
}

function StatusDonuts({ periodA, periodB }) {
  return (
    <Card className="border-[#E2E6D9]" data-testid="chart-status-compare">
      <CardHeader className="py-3">
        <CardTitle className="text-sm font-bold uppercase tracking-wide flex items-center gap-2">
          <Sparkles className="w-4 h-4 text-[#DAA520]" /> Order Status Split
        </CardTitle>
      </CardHeader>
      <CardContent className="pt-0">
        <div className="grid grid-cols-2 gap-2">
          <_StatusDonutOne p={periodA} sub="Current" />
          <_StatusDonutOne p={periodB} sub="Previous" />
        </div>
      </CardContent>
    </Card>
  );
}

// ------------------ side-by-side category pies ------------------
function CategoryPiesCompare({ periodA, periodB }) {
  return (
    <Card className="border-[#E2E6D9]" data-testid="chart-category-compare">
      <CardHeader className="py-3">
        <CardTitle className="text-sm font-bold uppercase tracking-wide flex items-center gap-2">
          <Boxes className="w-4 h-4 text-[#DAA520]" /> Value Share by Category
          <span className="text-[10px] font-normal text-[#8F9485] normal-case tracking-normal ml-1">(hover / tap a slice to explore)</span>
        </CardTitle>
      </CardHeader>
      <CardContent className="pt-0">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          <div>
            <p className="text-xs font-semibold text-[#556B2F] mb-1 text-center">Current — {rangeLabel(periodA.start, periodA.end)}</p>
            <AnimatedCategoryPie data={periodA.by_category} testId="compare-pie-a" compact />
          </div>
          <div>
            <p className="text-xs font-semibold text-[#8B6914] mb-1 text-center">Previous — {rangeLabel(periodB.start, periodB.end)}</p>
            <AnimatedCategoryPie data={periodB.by_category} testId="compare-pie-b" compact />
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

// ------------------ daily trend overlay ------------------
function DailyTrendOverlay({ periodA, periodB }) {
  const data = useMemo(() => {
    const max = Math.max(periodA.daily.length, periodB.daily.length);
    const rows = [];
    for (let i = 0; i < max; i++) {
      rows.push({
        day: `Day ${i + 1}`,
        a_value: periodA.daily[i]?.value || 0,
        b_value: periodB.daily[i]?.value || 0,
        a_date: periodA.daily[i]?.date || '',
        b_date: periodB.daily[i]?.date || '',
      });
    }
    return rows;
  }, [periodA, periodB]);
  return (
    <Card className="border-[#E2E6D9]" data-testid="chart-daily-overlay">
      <CardHeader className="py-3">
        <CardTitle className="text-sm font-bold uppercase tracking-wide flex items-center gap-2">
          <TrendingUp className="w-4 h-4 text-[#556B2F]" /> Daily Value Trend
          <span className="text-[10px] font-normal text-[#8F9485] normal-case tracking-normal ml-1">(both periods overlaid)</span>
        </CardTitle>
      </CardHeader>
      <CardContent className="pt-0">
        <div className="h-72">
          <ResponsiveContainer width="100%" height="100%">
            <LineChart data={data} margin={{ top: 8, right: 20, left: 0, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#E2E6D9" />
              <XAxis dataKey="day" tick={{ fontSize: 10, fill: '#5C6150' }} />
              <YAxis tickFormatter={short} tick={{ fontSize: 10, fill: '#5C6150' }} />
              <Tooltip
                // eslint-disable-next-line react/no-unstable-nested-components
                content={({ active, payload }) => {
                  if (!active || !payload || !payload.length) return null;
                  const p = payload[0].payload;
                  return (
                    <div className="rounded-md border border-[#E2E6D9] bg-white p-2 text-xs shadow-md">
                      <p className="font-bold text-[#1A1C16] mb-1">{p.day}</p>
                      {p.a_date && <p><span className="inline-block w-2 h-2 rounded-full mr-1.5" style={{ background: A_COLOR }} />{monthLabelShort(p.a_date)}: <b>{short(p.a_value)}</b></p>}
                      {p.b_date && <p><span className="inline-block w-2 h-2 rounded-full mr-1.5" style={{ background: B_COLOR }} />{monthLabelShort(p.b_date)}: <b>{short(p.b_value)}</b></p>}
                    </div>
                  );
                }}
              />
              <Legend wrapperStyle={{ fontSize: '11px' }} />
              <Line type="monotone" dataKey="a_value" name="Current" stroke={A_COLOR} strokeWidth={2.5} dot={{ r: 3 }} activeDot={{ r: 5 }} isAnimationActive animationDuration={800} />
              <Line type="monotone" dataKey="b_value" name="Previous" stroke={B_COLOR} strokeWidth={2.5} strokeDasharray="5 5" dot={{ r: 3 }} activeDot={{ r: 5 }} isAnimationActive animationDuration={800} />
            </LineChart>
          </ResponsiveContainer>
        </div>
      </CardContent>
    </Card>
  );
}

// ------------------ salesperson head-to-head bars ------------------
function SalespersonCompare({ periodA, periodB, onPresent }) {
  const data = useMemo(() => {
    const map = {};
    (periodA.by_salesperson || []).forEach(s => {
      map[s.user_id] = { name: s.name, user_id: s.user_id, a_value: s.value, a_count: s.count, a_closed: s.closed_count, a_closed_value: s.closed_value, b_value: 0, b_count: 0, b_closed: 0, b_closed_value: 0 };
    });
    (periodB.by_salesperson || []).forEach(s => {
      if (map[s.user_id]) Object.assign(map[s.user_id], { b_value: s.value, b_count: s.count, b_closed: s.closed_count, b_closed_value: s.closed_value });
      else map[s.user_id] = { name: s.name, user_id: s.user_id, a_value: 0, a_count: 0, a_closed: 0, a_closed_value: 0, b_value: s.value, b_count: s.count, b_closed: s.closed_count, b_closed_value: s.closed_value };
    });
    return Object.values(map).sort((a, b) => (b.a_value + b.b_value) - (a.a_value + a.b_value)).slice(0, 10);
  }, [periodA, periodB]);

  return (
    <Card className="border-[#E2E6D9]" data-testid="chart-salesperson-compare">
      <CardHeader className="py-3 flex-row items-center justify-between space-y-0">
        <CardTitle className="text-sm font-bold uppercase tracking-wide flex items-center gap-2">
          <Crown className="w-4 h-4 text-[#DAA520]" /> Salespeople — Head to Head
        </CardTitle>
        <p className="text-[10px] text-[#8F9485]">click a bar to open Presentation Mode</p>
      </CardHeader>
      <CardContent className="pt-0">
        {data.length === 0 && <p className="text-sm text-[#8F9485] py-4 text-center">No salesperson activity in this window.</p>}
        {data.length > 0 && (
          <div style={{ height: Math.max(240, data.length * 42) }}>
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={data} layout="vertical" margin={{ top: 8, right: 60, left: 4, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#E2E6D9" horizontal={false} />
                <XAxis type="number" tickFormatter={short} tick={{ fontSize: 10, fill: '#5C6150' }} />
                <YAxis type="category" dataKey="name" tick={{ fontSize: 11, fill: '#1A1C16' }} width={120} />
                <Tooltip
                  // eslint-disable-next-line react/no-unstable-nested-components
                  content={({ active, payload, label }) => {
                    if (!active || !payload || !payload.length) return null;
                    const p = payload[0].payload;
                    return (
                      <div className="rounded-md border border-[#E2E6D9] bg-white p-2 text-xs shadow-md">
                        <p className="font-bold text-[#1A1C16] mb-1">{label}</p>
                        <p><span className="inline-block w-2 h-2 rounded-full mr-1.5" style={{ background: A_COLOR }} />Current: <b>{short(p.a_value)}</b> · {p.a_count} quotes · {p.a_closed} closed</p>
                        <p><span className="inline-block w-2 h-2 rounded-full mr-1.5" style={{ background: B_COLOR }} />Previous: <b>{short(p.b_value)}</b> · {p.b_count} quotes · {p.b_closed} closed</p>
                        <p className="mt-1 text-[10px] text-[#DAA520]">Click bar → Presentation Mode</p>
                      </div>
                    );
                  }}
                />
                <Legend wrapperStyle={{ fontSize: '11px' }} />
                <Bar dataKey="a_value" name="Current" fill={A_COLOR} radius={[0, 4, 4, 0]} onClick={(d) => onPresent(d.user_id)} cursor="pointer" />
                <Bar dataKey="b_value" name="Previous" fill={B_COLOR} radius={[0, 4, 4, 0]} onClick={(d) => onPresent(d.user_id)} cursor="pointer" />
              </BarChart>
            </ResponsiveContainer>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

// ------------------ presentation mode ------------------
function PresentationMode({ userId, periodA, periodB, dates, onClose }) {
  const spA = (periodA.by_salesperson || []).find(s => s.user_id === userId);
  const spB = (periodB.by_salesperson || []).find(s => s.user_id === userId);
  const name = spA?.name || spB?.name || 'Salesperson';
  const staffCode = spA?.staff_code || spB?.staff_code || '';

  const [notes, setNotes] = useState('');
  const [exporting, setExporting] = useState(false);
  const [lastPdfUrl, setLastPdfUrl] = useState(null); // set after successful export; enables WhatsApp share hint

  const a = spA || { count: 0, value: 0, closed_count: 0, closed_value: 0 };
  const b = spB || { count: 0, value: 0, closed_count: 0, closed_value: 0 };

  const pct = (x, y) => (y > 0 ? Math.round(((x - y) / Math.abs(y)) * 100) : (x > 0 ? 100 : 0));
  const dQuotes = pct(a.count, b.count);
  const dValue = pct(a.value, b.value);
  const dClosed = pct(a.closed_count, b.closed_count);
  const dClosedValue = pct(a.closed_value, b.closed_value);
  const closeRateA = a.count > 0 ? ((a.closed_count / a.count) * 100).toFixed(1) : '0.0';
  const closeRateB = b.count > 0 ? ((b.closed_count / b.count) * 100).toFixed(1) : '0.0';

  // Auto-generated narrative — good vs growth takeaways
  const wins = [];
  const grow = [];
  if (dQuotes > 15) wins.push(`Raised ${dQuotes}% more quotations — great outreach.`);
  else if (dQuotes < -15) grow.push(`Quotation volume down ${Math.abs(dQuotes)}%. Aim for more customer conversations this period.`);
  if (dClosedValue > 15) wins.push(`Closed ₹ up ${dClosedValue}% — strong deal-closing this period.`);
  else if (dClosedValue < -15 && b.closed_value > 0) grow.push(`Closed value down ${Math.abs(dClosedValue)}%. Focus on following up pending quotes and pushing high-value deals.`);
  if (Number(closeRateA) > Number(closeRateB) + 3) wins.push(`Close rate improved from ${closeRateB}% → ${closeRateA}% — you're converting better.`);
  else if (Number(closeRateB) > Number(closeRateA) + 3) grow.push(`Close rate dropped from ${closeRateB}% → ${closeRateA}%. Review lost reasons to spot patterns.`);
  if (a.value > b.value * 1.5 && b.value > 0) wins.push(`Total value more than 1.5× higher than last period — excellent momentum.`);
  if (!wins.length && !grow.length) wins.push('Steady performance — keep the consistent output.');

  const exportPdf = async () => {
    setExporting(true);
    try {
      const res = await api.exportPresentationPdf({
        user_id: userId,
        a_start: dates.a_start, a_end: dates.a_end,
        b_start: dates.b_start, b_end: dates.b_end,
        notes,
      });
      // Kick off a browser download from the blob response.
      const blob = new Blob([res.data], { type: 'application/pdf' });
      const url = URL.createObjectURL(blob);
      const filename = `HEDA_review_${(name || 'staff').replace(/\s+/g, '_')}_${dates.a_start}_vs_${dates.b_start}.pdf`;
      const link = document.createElement('a');
      link.href = url; link.download = filename;
      document.body.appendChild(link); link.click(); link.remove();
      setLastPdfUrl(url);
      toast.success('PDF downloaded — ready to share on WhatsApp.');
    } catch (e) {
      logger.warn('presentation pdf export failed', e);
      toast.error(e.response?.data?.detail || 'PDF export failed');
    } finally {
      setExporting(false);
    }
  };

  const shareWhatsApp = () => {
    const period = `${rangeLabel(dates.a_start, dates.a_end)} vs ${rangeLabel(dates.b_start, dates.b_end)}`;
    const msg = encodeURIComponent(
      `Hi ${name.split(' ')[0]}, here's your performance review for ${period}.\n\n` +
      `📊 ${a.count} quotations (Prev: ${b.count})\n` +
      `💰 Value: ${short(a.value)} (Prev: ${short(b.value)})\n` +
      `✅ Closed: ${a.closed_count} (Prev: ${b.closed_count})\n\n` +
      `Attaching the detailed PDF review. Let's chat about the takeaways when you get a moment 🙂\n\n— HEDA PLYWOODS`
    );
    // Open a fresh chat with the message pre-filled; owner then attaches
    // the downloaded PDF from their file browser (WhatsApp Web/native).
    window.open(`https://wa.me/?text=${msg}`, '_blank', 'noopener');
  };

  return (
    <motion.div
      initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
      className="fixed inset-0 bg-white z-50 overflow-y-auto"
      data-testid="presentation-mode"
    >
      <div className="max-w-4xl mx-auto p-6 sm:p-10">
        <div className="flex items-center justify-between mb-6">
          <Button variant="ghost" onClick={onClose} className="text-[#5C6150]" data-testid="presentation-close">
            <ArrowLeft className="w-4 h-4 mr-1.5" /> Back to comparison
          </Button>
          <p className="text-xs text-[#8F9485] uppercase tracking-wider">Presentation Mode</p>
        </div>

        {/* Header */}
        <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}>
          <div className="flex items-center gap-3 mb-1">
            <div className="w-14 h-14 rounded-full bg-gradient-to-br from-[#556B2F] to-[#8FA057] flex items-center justify-center">
              <User className="w-7 h-7 text-white" />
            </div>
            <div>
              <h1 className="text-3xl font-bold text-[#1A1C16] leading-tight">{name}</h1>
              {staffCode && <p className="text-sm text-[#8F9485] font-mono">{staffCode}</p>}
            </div>
          </div>
          <p className="text-sm text-[#5C6150] mt-3">
            Reviewing performance:
            <span className="mx-1 font-semibold text-[#556B2F]">{rangeLabel(dates.a_start, dates.a_end)}</span>
            vs
            <span className="mx-1 font-semibold text-[#8B6914]">{rangeLabel(dates.b_start, dates.b_end)}</span>
          </p>
        </motion.div>

        {/* Big cards */}
        <div className="grid grid-cols-2 gap-3 mt-6">
          <BigTile label="Quotations Raised" valueA={a.count} valueB={b.count} delta={dQuotes} tone="olive" testId="pres-tile-quotes" />
          <BigTile label="Quotation Value" valueA={a.value} valueB={b.value} delta={dValue} format={short} tone="blue" testId="pres-tile-value" />
          <BigTile label="Deals Closed" valueA={a.closed_count} valueB={b.closed_count} delta={dClosed} tone="green" testId="pres-tile-closed" />
          <BigTile label="Closed Value" valueA={a.closed_value} valueB={b.closed_value} delta={dClosedValue} format={short} tone="green" sub={`Close rate ${closeRateA}%`} testId="pres-tile-closed-value" />
        </div>

        {/* Takeaways */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mt-6">
          <motion.div initial={{ opacity: 0, x: -8 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: 0.2 }} className="rounded-2xl border-2 border-[#2E7D32]/30 bg-gradient-to-br from-[#2E7D32]/5 to-transparent p-5" data-testid="pres-wins">
            <div className="flex items-center gap-2 mb-3">
              <div className="p-1.5 rounded-lg bg-[#2E7D32]/10">
                <ThumbsUp className="w-5 h-5 text-[#2E7D32]" />
              </div>
              <h3 className="text-lg font-bold text-[#2E7D32]">Wins to celebrate</h3>
            </div>
            {wins.length === 0 ? (
              <p className="text-sm text-[#8F9485]">Nothing to highlight this period — see growth areas.</p>
            ) : (
              <ul className="space-y-2 text-sm text-[#1A1C16]">
                {wins.map((w, i) => (
                  <li key={i} className="flex gap-2">
                    <CheckCircle2 className="w-4 h-4 text-[#2E7D32] flex-shrink-0 mt-0.5" />
                    <span>{w}</span>
                  </li>
                ))}
              </ul>
            )}
          </motion.div>
          <motion.div initial={{ opacity: 0, x: 8 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: 0.3 }} className="rounded-2xl border-2 border-[#DAA520]/40 bg-gradient-to-br from-[#DAA520]/5 to-transparent p-5" data-testid="pres-grow">
            <div className="flex items-center gap-2 mb-3">
              <div className="p-1.5 rounded-lg bg-[#DAA520]/10">
                <Target className="w-5 h-5 text-[#8B6914]" />
              </div>
              <h3 className="text-lg font-bold text-[#8B6914]">Growth areas</h3>
            </div>
            {grow.length === 0 ? (
              <p className="text-sm text-[#8F9485]">All key indicators are steady or improving — keep it up.</p>
            ) : (
              <ul className="space-y-2 text-sm text-[#1A1C16]">
                {grow.map((g, i) => (
                  <li key={i} className="flex gap-2">
                    <Target className="w-4 h-4 text-[#8B6914] flex-shrink-0 mt-0.5" />
                    <span>{g}</span>
                  </li>
                ))}
              </ul>
            )}
          </motion.div>
        </div>

        {/* Owner's meeting notes + export controls */}
        <div className="mt-6 rounded-2xl border border-[#E2E6D9] bg-white p-4 sm:p-5" data-testid="pres-export-section">
          <div className="flex items-center justify-between gap-2 flex-wrap mb-2">
            <div>
              <p className="text-[11px] uppercase tracking-widest text-[#8F9485] font-bold">Meeting notes (optional)</p>
              <p className="text-[11px] text-[#5C6150]">Anything you discussed — goals for the next period, specific quotes to follow up, etc. This gets printed into the PDF.</p>
            </div>
            <div className="flex items-center gap-2">
              <Button
                onClick={exportPdf}
                disabled={exporting}
                className="bg-gradient-to-r from-[#556B2F] to-[#8FA057] hover:from-[#3F5222] hover:to-[#556B2F] text-white text-xs h-9"
                data-testid="pres-export-pdf-btn"
              >
                <FileDown className="w-4 h-4 mr-1.5" />
                {exporting ? 'Building PDF…' : 'Download PDF'}
              </Button>
              <Button
                onClick={shareWhatsApp}
                variant="outline"
                className="text-xs h-9 border-[#25D366]/50 text-[#0f6d40] hover:bg-[#25D366]/10"
                data-testid="pres-share-whatsapp-btn"
              >
                <MessageCircle className="w-4 h-4 mr-1.5" />
                Share on WhatsApp
              </Button>
            </div>
          </div>
          <Textarea
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            placeholder="e.g. Follow up with 5 pending quotes worth >₹50K each. Target: 3 closes by month-end. Reduce discount range for veneer."
            rows={3}
            className="text-sm"
            data-testid="pres-notes-textarea"
          />
          {lastPdfUrl && (
            <p className="text-[11px] text-[#2E7D32] mt-2">
              ✓ PDF ready. Click <b>Share on WhatsApp</b> above → then attach the downloaded PDF from your files in the WhatsApp window that opens.
            </p>
          )}
        </div>

        {/* Numeric summary strip for the record */}
        <div className="mt-6 rounded-xl border border-[#E2E6D9] bg-[#F9FAF7] p-4">
          <p className="text-[10px] uppercase tracking-widest text-[#8F9485] font-bold mb-2">Numeric summary</p>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 text-xs">
            <div>
              <p className="text-[#5C6150] font-semibold">Quotes</p>
              <p className="text-[#1A1C16] font-mono-data">{a.count} <span className="text-[#8F9485]">vs</span> {b.count}</p>
            </div>
            <div>
              <p className="text-[#5C6150] font-semibold">Value</p>
              <p className="text-[#1A1C16] font-mono-data">{short(a.value)} <span className="text-[#8F9485]">vs</span> {short(b.value)}</p>
            </div>
            <div>
              <p className="text-[#5C6150] font-semibold">Closed</p>
              <p className="text-[#1A1C16] font-mono-data">{a.closed_count} <span className="text-[#8F9485]">vs</span> {b.closed_count}</p>
            </div>
            <div>
              <p className="text-[#5C6150] font-semibold">Close rate</p>
              <p className="text-[#1A1C16] font-mono-data">{closeRateA}% <span className="text-[#8F9485]">vs</span> {closeRateB}%</p>
            </div>
          </div>
        </div>
      </div>
    </motion.div>
  );
}

// ==================== MAIN ====================
export default function ComparisonAnalyticsView() {
  const navigate = useNavigate();
  const [preset, setPreset] = useState('mtd');
  const [today] = useState(() => isoToday());
  const presets = useMemo(() => buildPresets(today), [today]);
  const [dates, setDates] = useState(() => {
    const p = presets.mtd;
    return { a_start: p.a_start, a_end: p.a_end, b_start: p.b_start, b_end: p.b_end };
  });
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [presentUserId, setPresentUserId] = useState(null);

  const applyPreset = useCallback((key) => {
    setPreset(key);
    if (key !== 'custom') {
      const p = presets[key];
      setDates({ a_start: p.a_start, a_end: p.a_end, b_start: p.b_start, b_end: p.b_end });
    }
  }, [presets]);

  useEffect(() => {
    setLoading(true);
    setError('');
    api.compareAnalyticsPeriods(dates)
      .then(r => setData(r.data))
      .catch(e => {
        logger.warn('compare fetch failed', e);
        setError(e.response?.data?.detail || 'Failed to load comparison');
      })
      .finally(() => setLoading(false));
  }, [dates]);

  // Salesperson dropdown options — union of both period's salespeople.
  // MUST be declared BEFORE any early return to satisfy rules-of-hooks.
  const salespeopleOpts = useMemo(() => {
    if (!data) return [];
    const m = {};
    (data.period_a?.by_salesperson || []).forEach(s => { m[s.user_id] = s.name; });
    (data.period_b?.by_salesperson || []).forEach(s => { m[s.user_id] = s.name; });
    return Object.entries(m).map(([id, name]) => ({ id, name }));
  }, [data]);

  if (loading) {
    return (
      <div className="space-y-3" data-testid="compare-loading">
        <Skeleton className="h-24" />
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">{[1,2,3,4].map(i => <Skeleton key={i} className="h-24" />)}</div>
        <Skeleton className="h-72" />
      </div>
    );
  }
  if (error || !data) return <p className="text-sm text-[#B93838] py-6 text-center" data-testid="compare-error">{error || 'No data'}</p>;

  const { period_a, period_b, deltas, alerts } = data;

  return (
    <div className="space-y-4" data-testid="comparison-analytics">
      <div className="flex items-center justify-between gap-2">
        <Button variant="ghost" size="sm" onClick={() => navigate(-1)} data-testid="back-to-analytics">
          <ArrowLeft className="w-4 h-4 mr-1.5" /> Back to Analytics
        </Button>
        <div className="flex items-center gap-2">
          {salespeopleOpts.length > 0 && (
            <Select onValueChange={(v) => setPresentUserId(v)}>
              <SelectTrigger className="h-8 text-xs w-56" data-testid="present-select">
                <Presentation className="w-3.5 h-3.5 mr-1.5" />
                <SelectValue placeholder="Present to a salesperson…" />
              </SelectTrigger>
              <SelectContent>
                {salespeopleOpts.map(s => (
                  <SelectItem key={s.id} value={s.id} data-testid={`present-option-${s.id}`}>{s.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}
        </div>
      </div>

      <PeriodPicker preset={preset} dates={dates} onPreset={applyPreset} onDatesChange={(d) => { setPreset('custom'); setDates(d); }} />

      {alerts && alerts.length > 0 && (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2" data-testid="compare-alerts">
          {alerts.map((a, i) => {
            const icons = { good: CheckCircle2, warn: AlertTriangle, bad: TrendingDown };
            const cls = { good: 'from-[#2E7D32]/10 to-[#2E7D32]/5 border-[#2E7D32]/30 text-[#2E7D32]', warn: 'from-[#DAA520]/10 to-[#DAA520]/5 border-[#DAA520]/40 text-[#8B6914]', bad: 'from-[#C62828]/10 to-[#C62828]/5 border-[#C62828]/30 text-[#C62828]' };
            const Icon = icons[a.level] || AlertTriangle;
            return (
              <motion.div key={i} initial={{ opacity: 0, x: -8 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: i * 0.08 }} className={`rounded-lg border bg-gradient-to-br ${cls[a.level] || cls.warn} p-3 flex gap-2`}>
                <Icon className="w-4 h-4 flex-shrink-0 mt-0.5" />
                <div className="min-w-0">
                  <p className="text-xs font-bold leading-tight">{a.title}</p>
                  <p className="text-[11px] text-[#5C6150] mt-0.5 leading-snug">{a.detail}</p>
                </div>
              </motion.div>
            );
          })}
        </div>
      )}

      {/* Only 4 headline tiles — the rest fold into subtitles */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <BigTile label="New Quotations" valueA={period_a.summary.total_quotations} valueB={period_b.summary.total_quotations} delta={deltas.total_quotations} tone="olive" sub={`Value ${short(period_a.summary.total_value)}`} testId="metric-quotations" />
        <BigTile label="Closed Value" valueA={period_a.summary.closed_value} valueB={period_b.summary.closed_value} delta={deltas.closed_value} format={short} tone="green" sub={`${period_a.summary.closed_count} deals`} testId="metric-closed-value" />
        <BigTile label="Lost Value" valueA={period_a.summary.lost_value} valueB={period_b.summary.lost_value} delta={deltas.lost_value} format={short} tone="red" sub={`${period_a.summary.lost_count} lost`} testId="metric-lost-value" />
        <BigTile label="Conversion" valueA={period_a.summary.conversion_rate} valueB={period_b.summary.conversion_rate} delta={deltas.conversion_rate} format={(n) => `${Number(n).toFixed(1)}%`} tone="blue" sub={`Avg quote ${short(period_a.summary.avg_value)}`} testId="metric-conversion" />
      </div>

      {/* Pipeline Health belongs here on the deep-dive page */}
      <PipelineHealthCard />

      {/* Daily trend + status donuts side-by-side */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <div className="lg:col-span-2">
          <DailyTrendOverlay periodA={period_a} periodB={period_b} />
        </div>
        <StatusDonuts periodA={period_a} periodB={period_b} />
      </div>

      {/* Category comparison — pie charts side-by-side, per owner ask */}
      <CategoryPiesCompare periodA={period_a} periodB={period_b} />

      {/* Salesperson head-to-head */}
      <SalespersonCompare periodA={period_a} periodB={period_b} onPresent={setPresentUserId} />

      {/* Presentation Mode overlay */}
      <AnimatePresence>
        {presentUserId && (
          <PresentationMode
            userId={presentUserId}
            periodA={period_a}
            periodB={period_b}
            dates={dates}
            onClose={() => setPresentUserId(null)}
          />
        )}
      </AnimatePresence>
    </div>
  );
}
